import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTenant } from "../tenant/TenantContext";
import { useBrand } from "../context/BrandContext";
import { resolveActiveBrandChrome } from "../activeBrandChrome";
import { CLIENT_KIND } from "../tenant/experiences/clientExperience";
import { isBrandSupportPath } from "../supportHubPaths";

export const CLIENT_NO_BRAND_FALLBACK_PATH = "/brand-manager";

/**
 * Whether this path is a Client Brand-scoped brand-shell page that cannot
 * render without a selected canonical Brand.
 *
 * Vendor kinds use /master-account/* without the Client Brand model.
 * Exempt Client paths:
 *   - /master-account/support: re-establishes Brand via authorized ?brandId=
 *     or fails closed to the Client Support Hub itself (BrandSupport).
 *   - /master-account/account*: legacy redirect to global Brand Manager Account.
 */
export function requiresClientBrandContext(tenantKind, pathname) {
  if (tenantKind !== CLIENT_KIND) return false;
  const p = String(pathname || "").split("?")[0].replace(/\/$/, "");
  if (p !== "/master-account" && !p.startsWith("/master-account/")) return false;
  if (isBrandSupportPath(p)) return false;
  if (p === "/master-account/account" || p.startsWith("/master-account/account/")) {
    return false;
  }
  return true;
}

/**
 * Client tenants with no valid selected Brand never see an empty Brand shell:
 * fail closed to Brand Manager, where the user explicitly selects a Brand.
 * Never selects, fabricates, or grants a Brand.
 */
export default function RequireClientBrandContext({ children }) {
  const location = useLocation();
  const { tenant } = useTenant();
  const { currentBrand } = useBrand();

  if (
    requiresClientBrandContext(tenant?.kind, location.pathname) &&
    !resolveActiveBrandChrome(currentBrand)
  ) {
    return <Navigate to={CLIENT_NO_BRAND_FALLBACK_PATH} replace />;
  }

  return children;
}
